/**
 * Single Row Component (Data List)
 */

import { IBuilderComponent } from '../../base'
import type {
  ResponsiveValue,
  SpacingInputValue,
  TableDataSource,
  TextAlignment,
  TypographyValue,
  VariableTextValue,
} from '../../types'
import {
  deepMerge,
  parseAlignmentFormat,
  parseResponsiveFormat,
  parseSpacingFormat,
  parseTableDataSource,
  parseToVariableText,
  parseTypographyFormat,
  parseWidthFormat,
} from '../../parser-utils'
import {
  formatFromVariableText,
  formatSpacingFormat,
  formatTableDataSource,
  formatTextAlignmentFormat,
  formatTypographyFormat,
  formatWidthFormat,
  formatResponsiveValue,
} from '../../format-utils'
import singleRowTemplate from './single-row.template.json'

export type { TableDataSource as DataListDataSource } from '../../types'

const SINGLE_ROW_DEFAULT_TEMPLATE = singleRowTemplate as unknown as ISingleRowComponentSettings

// todo: convert the datasource structure `columns` into array

export interface ISingleRowComponentSettings {
  // Data settings
  'data-list-data': TableDataSource
  'data-list-text': VariableTextValue

  // Design settings
  'data-list-design-width': ResponsiveValue<string[] | number[]>
  'data-list-design-h-align': ResponsiveValue<TextAlignment>
  'data-list-design-headline-align': ResponsiveValue<any>
  'data-list-design-title-alignment': ResponsiveValue<boolean>
  'data-list-design-content-alignment': ResponsiveValue<boolean>
  'data-list-design-margin': ResponsiveValue<SpacingInputValue>
  'data-list-design-headline-margin': ResponsiveValue<SpacingInputValue>
  'data-list-design-rounded-corner': string
  'data-list-design-border-variant': string
  'data-list-design-variant': string
  'data-list-design-color-1': any
  'data-list-design-color-2': any
  'data-list-design-typography': {
    'data-list-design-content-typography': ResponsiveValue<TypographyValue>
    'data-list-design-title-typography': ResponsiveValue<TypographyValue>
  }
}

export interface ISingleRowComponent extends IBuilderComponent {
  componentId: 'data-list'
  groupId: 'table'
  settings: ISingleRowComponentSettings
}

/**
 * Single Row AI Input
 */
export interface AiSingleRowInput {
  dataSource: string | any // Table ID (string), simplified structure (same as data-table), or full complex object
  text?: string[] // Format: ["text:Headline"] or ["text:Details for ", "cr:item"]

  // Styling
  width?: string // Format: "w:100%" or "w:100% de:w:60%"
  alignment?: string // Format: "center" or "left"
  headlineAlignment?: string // Headline alignment
  titleAlignment?: boolean | string // Title alignment (boolean or responsive string)
  contentAlignment?: boolean | string // Content alignment (boolean or responsive string)
  margin?: string // Format: "m:16px"
  headlineMargin?: string // Headline margin
  cornerRadius?: string // Corner radius
  borderVariant?: string // Border variant: "all", "none", etc.
  variant?: string // Variant: "stripped", "bordered", etc.
  color1?: string // Primary color
  color2?: string // Secondary color

  // Typography
  typography?: {
    title?: string // Title typography
    content?: string // Content typography
  }
}

export class SingleRowComp {
  getTemplate(): ISingleRowComponentSettings {
    return SINGLE_ROW_DEFAULT_TEMPLATE
  }

  /**
   * format single row component settings to AI single row input
   */
  format(settings: ISingleRowComponentSettings): AiSingleRowInput {
    const result: AiSingleRowInput = {
      dataSource: '',
    }

    // Data - Source
    if (settings['data-list-data']) {
      const formatted = formatTableDataSource(settings['data-list-data'])
      result.dataSource = formatted || ''
    }

    // Data - Text
    if (settings['data-list-text']) {
      result.text = formatFromVariableText(settings['data-list-text'])
    }

    // Design - Width
    if (settings['data-list-design-width']) {
      result.width = formatWidthFormat(settings['data-list-design-width'])
    }

    // Design - Alignment
    if (settings['data-list-design-h-align']) {
      result.alignment = formatTextAlignmentFormat(settings['data-list-design-h-align'])?.replace(/^t:/, '') || undefined
    }

    if (settings['data-list-design-headline-align']) {
      result.headlineAlignment = formatTextAlignmentFormat(settings['data-list-design-headline-align'] as any)?.replace(/^t:/, '') || undefined
    }

    // Design - Title/Content Alignment (boolean responsive)
    if (settings['data-list-design-title-alignment']) {
      const allTrue = Object.values(settings['data-list-design-title-alignment']).every((v) => v === true)
      const allFalse = Object.values(settings['data-list-design-title-alignment']).every((v) => v === false)
      if (allTrue || allFalse) {
        result.titleAlignment = allTrue
      } else {
        result.titleAlignment = formatResponsiveValue(settings['data-list-design-title-alignment'], (val) => String(val))
      }
    }

    if (settings['data-list-design-content-alignment']) {
      const allTrue = Object.values(settings['data-list-design-content-alignment']).every((v) => v === true)
      const allFalse = Object.values(settings['data-list-design-content-alignment']).every((v) => v === false)
      if (allTrue || allFalse) {
        result.contentAlignment = allTrue
      } else {
        result.contentAlignment = formatResponsiveValue(settings['data-list-design-content-alignment'], (val) => String(val))
      }
    }

    // Design - Margin
    if (settings['data-list-design-margin']) {
      result.margin = formatSpacingFormat(settings['data-list-design-margin'])
    }

    if (settings['data-list-design-headline-margin']) {
      result.headlineMargin = formatSpacingFormat(settings['data-list-design-headline-margin'])
    }

    // Design - Variant & Style
    if (settings['data-list-design-variant']) {
      result.variant = settings['data-list-design-variant']
    }

    if (settings['data-list-design-border-variant']) {
      result.borderVariant = settings['data-list-design-border-variant']
    }

    if (settings['data-list-design-rounded-corner']) {
      result.cornerRadius = settings['data-list-design-rounded-corner']
    }

    // Design - Colors
    if (settings['data-list-design-color-1']) {
      result.color1 = settings['data-list-design-color-1']
    }

    if (settings['data-list-design-color-2']) {
      result.color2 = settings['data-list-design-color-2']
    }

    // Design - Typography
    if (settings['data-list-design-typography']) {
      result.typography = {}
      if (settings['data-list-design-typography']['data-list-design-title-typography']) {
        result.typography.title = formatTypographyFormat(settings['data-list-design-typography']['data-list-design-title-typography'])
      }
      if (settings['data-list-design-typography']['data-list-design-content-typography']) {
        result.typography.content = formatTypographyFormat(settings['data-list-design-typography']['data-list-design-content-typography'])
      }
    }

    return result
  }

  /**
   * parse AI single row input to single row component settings
   */
  parse(input: AiSingleRowInput): ISingleRowComponentSettings {
    const result = deepMerge(SINGLE_ROW_DEFAULT_TEMPLATE, {})

    // Data - Source (required)
    if (input.dataSource) {
      result['data-list-data'] = parseTableDataSource(input.dataSource, {
        tableKey: 'table',
        includeIcon: false,
        isDetailPageTable: false,
      })
    }

    // Data - Text
    if (input.text !== undefined) {
      result['data-list-text'] = parseToVariableText(input.text)
    }

    // Design - Width
    if (input.width) {
      result['data-list-design-width'] = parseWidthFormat(input.width)
    }

    // Design - Alignment
    if (input.alignment) {
      const alignmentParsed = parseAlignmentFormat(input.alignment)
      if (alignmentParsed.textAlign) {
        result['data-list-design-h-align'] = alignmentParsed.textAlign
      }
    }

    if (input.headlineAlignment) {
      const headlineAlignParsed = parseAlignmentFormat(input.headlineAlignment)
      if (headlineAlignParsed.textAlign) {
        result['data-list-design-headline-align'] = headlineAlignParsed.textAlign
      }
    }

    // Design - Title/Content Alignment (boolean responsive)
    if (input.titleAlignment !== undefined) {
      if (typeof input.titleAlignment === 'boolean') {
        result['data-list-design-title-alignment'] = {
          phone: input.titleAlignment,
          'phone-landscape': input.titleAlignment,
          tablet: input.titleAlignment,
          'tablet-landscape': input.titleAlignment,
          desktop: input.titleAlignment,
          tv: input.titleAlignment,
        }
      } else {
        // Responsive string format
        result['data-list-design-title-alignment'] = parseResponsiveFormat(
          input.titleAlignment,
          (val: string) => val === 'true'
        )
      }
    }

    if (input.contentAlignment !== undefined) {
      if (typeof input.contentAlignment === 'boolean') {
        result['data-list-design-content-alignment'] = {
          phone: input.contentAlignment,
          'phone-landscape': input.contentAlignment,
          tablet: input.contentAlignment,
          'tablet-landscape': input.contentAlignment,
          desktop: input.contentAlignment,
          tv: input.contentAlignment,
        }
      } else {
        // Responsive string format
        result['data-list-design-content-alignment'] = parseResponsiveFormat(
          input.contentAlignment,
          (val: string) => val === 'true'
        )
      }
    }

    // Design - Margin
    if (input.margin) {
      result['data-list-design-margin'] = parseSpacingFormat(input.margin)
    }

    if (input.headlineMargin) {
      result['data-list-design-headline-margin'] = parseSpacingFormat(input.headlineMargin)
    }

    // Design - Variant & Style
    if (input.variant) {
      result['data-list-design-variant'] = input.variant
    }

    if (input.borderVariant) {
      result['data-list-design-border-variant'] = input.borderVariant
    }

    if (input.cornerRadius) {
      result['data-list-design-rounded-corner'] = input.cornerRadius
    }

    // Design - Colors
    if (input.color1) {
      result['data-list-design-color-1'] = input.color1
    }

    if (input.color2) {
      result['data-list-design-color-2'] = input.color2
    }

    // Design - Typography
    if (input.typography) {
      if (input.typography.title) {
        const titleTypo = parseTypographyFormat(input.typography.title)
        result['data-list-design-typography']['data-list-design-title-typography'] = titleTypo
      }

      if (input.typography.content) {
        const contentTypo = parseTypographyFormat(input.typography.content)
        result['data-list-design-typography']['data-list-design-content-typography'] = contentTypo
      }
    }

    return result
  }
}
